import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route } from "react-router-dom";
import NotFound from "routes/NotFound";
import ForgotPassword from "routes/ForgotPassword";
import ResetPassword from "routes/ResetPassword";
import Onboarding from "routes/Onboarding";
import GridLayout from "layouts/Grid/GridLayout";
import Case from "./routes/lenses/lens/case/Case";
import Lenses from "routes/lenses/Lenses";
import ImportHistory from "routes/case-import/ImportHistory";
import { appLoader, checkSignedInLoader, shouldRevalidateApp } from "loaders/app.loader";
import { lensLoader, shouldRevalidateLens } from "loaders/lens.loader";
import PatternError from "components/PatternError";
import Documents from "routes/lenses/lens/case/routes/Documents";
import Review from "routes/lenses/lens/case/routes/Review";
import ReadQuality from "routes/lenses/lens/case/routes/ReadQuality";
import Cases from "routes/lenses/lens/case/Cases";
import ProfileExport from "routes/lenses/lens/case/ProfileExport";
import { caseLoader, shouldRevalidateCase } from "loaders/case.loader";
import CaseProfileRoute from "routes/lenses/lens/case/routes/CaseProfileRoute";
import CaseReviewRoute from "routes/lenses/lens/case/routes/CaseReviewRoute";
import LensHome from "routes/lenses/lens/LensHome";
import PatternInvite from "routes/users/pattern/PatternInvite";
import PatternUsers from "routes/users/pattern/PatternUsers";
import PatternUser from "routes/users/pattern/PatternUser";
import UserProfile from "routes/users/pattern/components/UserProfile";
import UserLensAccess from "routes/users/pattern/components/UserLensAccess";
import UserApiAccess from "routes/users/pattern/components/UserApiAccess";
import CollectUsers from "routes/users/collect/CollectUsers";
import Users from "routes/users/Users";
import FirmManagement from "routes/case-import/FirmManagement";
import { lensEditorLensesLoader, shouldRevalidateLenses } from "loaders/lenses.loader";
import CaseImport from "routes/lenses/lens/case-import/CaseImport";
import Logout from "routes/Logout";
import LensDocuments from "routes/lenses/lens/files/LensDocuments";
import LensFiles from "routes/lenses/lens/files/LensFiles";
import LensExports from "routes/lenses/lens/files/LensExports";
import LensRules from "routes/lens-editor/LensRules";
import Duplicates from "routes/lens-editor/Duplicates";
import ChronologyExportBuilder from "routes/lens-editor/ChronologyExportBuilder";
import LensEditorLenses from "routes/lens-editor/Lenses";
import LensEditor from "routes/lens-editor/LensEditor";
import { lensEditorLoader, shouldRevalidateLensEditor } from "loaders/lenseditor.loader";
import AiChatBrowser from "routes/lens-editor/ai-chats/AiChatBrowser";
import AITab from "routes/lens-editor/AITab";
import { AiChat } from "routes/lens-editor/ai-chats/AiChat";
import { aiChatLoader, shouldRevalidateAiChat } from "routes/lens-editor/ai-chats/aiChatLoader";
import Report from "routes/lenses/lens/report/Report";
import { reportLoader } from "loaders/report.loader";
import { LensHomeBreadcrumb } from "components/Breadcrumbs/LensHome.breadcrumb";
import { CaseHomeBreadcrumb } from "components/Breadcrumbs/CaseHome.breadcrumb";
import { ReportBreadcrumb } from "components/Breadcrumbs/Report.breadcrumb";
import { LensSubnavBreadcrumb } from "components/Breadcrumbs/LensSubnav.breadcrumb";
import ReportsHome from "./components/Looker/ReportsHome";
import { CaseSubnavBreadcrumb } from "./components/Breadcrumbs/CaseSubnav.breadcrumb";
import { FilesSubnavBreadcrumb } from "./components/Breadcrumbs/FilesSubnav.breadcrumb";
import BreadcrumbDropdownPicker from "components/Breadcrumbs/BreadcrumbDropdownPicker";
import Scripts from "./routes/lens-editor/Scripts";
import PageLabelRoute from "routes/lens-editor/page-labels/PageLabelRoute";
import PageLabels from "routes/lens-editor/page-labels/PageLabels";
import {
  pageLabelRuleAction,
  pageLabelsAction,
  pageLabelsImportAction,
  pageLabelsLoader,
} from "loaders/page-labels.loader";
import PageLabelsHeader from "routes/lens-editor/page-labels/PageLabelsHeader";
import PageLabelsImport from "routes/lens-editor/page-labels/PageLabelsImport";
import {
  caseDocumentExternalLinkLoader,
  caseDocumentsExternalLinkLoader,
  caseExternalLinkLoader,
  shouldRevalidateDeepLinks,
} from "loaders/deep-links.loader";
import { scriptsAction, scriptsHistoryLoader, scriptsLoader } from "loaders/scripts.loader";
import ScriptRoute from "routes/lens-editor/scripts/ScriptRoute";
import { LensEditorBreadcrumb } from "components/Breadcrumbs/lens-editor/LensEditor.breadcrumb";
import { LensEditorSubnavBreadcrumb } from "components/Breadcrumbs/lens-editor/LensEditorSubnav.breadcrumb";
import { LensEditorHomeBreadcrumb } from "components/Breadcrumbs/lens-editor/LensEditorHome.breadcrumb";
import Login from "routes/Login";
import LensSettings from "routes/lens-editor/settings/LensSettings";
import ScriptsHeader from "routes/lens-editor/scripts/ScriptsHeader";
import { ScriptsHistory } from "routes/lens-editor/scripts/History";
import UserAdvanced from "routes/users/pattern/components/UserAdvanced";
import { CaseProfileField } from "routes/lenses/lens/case/field/CaseProfileField";
import { CasePropertiesHeader } from "routes/lens-editor/custom-properties/CustomPropertiesHeader";
import { CustomCaseProperties } from "routes/lens-editor/custom-properties/CustomCaseProperties";
import { CustomDocumentProperties } from "routes/lens-editor/custom-properties/CustomDocumentProperties";
import { customPropertiesLoader } from "loaders/custom-properties.loader";
import { CustomCalcProperties } from "routes/lens-editor/custom-properties/CustomCalcProperties";
import Stats from "routes/lens-editor/Stats";
import ProfileBuilderSections from "routes/lens-editor/profile-builder/ProfileBuilderSections";
import ProfileBuilderField from "routes/lens-editor/profile-builder/ProfileBuilderField";
import ProfileBuilderCell from "routes/lens-editor/profile-builder/ProfileBuilderCell";
import ProfileBuilderRoot from "routes/lens-editor/profile-builder/ProfileBuilderRoot";
import { lenseditorProfileCellLoader } from "loaders/lenseditor.profile.cell.loader";
import LensSpecification from "routes/lenses/lens/LensSpecification";
import { lensSpecLoader } from "loaders/lens-specification.loader";
import { dashboardLoader, shouldRevalidateDashboard } from "loaders/dashboard.loader";
import ProfileBuilderFieldSettings from "routes/lens-editor/profile-builder/ProfileBuilderFieldSettings";
import ProfileBuilderFieldWrapper from "routes/lens-editor/profile-builder/ProfileBuilderFieldWrapper";
import { ProfileBuilderCells } from "routes/lens-editor/profile-builder/ProfileBuilderCells";
import Import from "routes/case-import/Import";
import {
  documentReviewLoader,
  shouldRevalidateDocumentReviewLoader,
} from "routes/lenses/lens/case/routes/review.loader";
import CaseDisplayGroup from "routes/lens-editor/custom-properties/CaseDisplayGroup";
import * as Sentry from "@sentry/react";
import ProtectedRoute from "ProtectedRoute";
import { Dashboard, Help, ScratchView } from "LazyRoutes";
import ProfileGenerationTestsRoute from "./routes/lens-editor/ai-chats/ProfileGenerationTestsRoute";
import ProfileGenerationRoute from "routes/lens-editor/ai-chats/ProfileGenerationRoute";
import CollectInvites from "routes/users/collect/CollectInvites";
import { LensRoute } from "routes/lenses/lens/LensRoute";
import DisplayGroups from "routes/lens-editor/custom-properties/DisplayGroups";
import GroupLocations from "routes/lens-editor/custom-properties/GroupLocations";
import SystemPageLabels from "routes/lens-editor/page-labels/SystemPageLabels";
import ProfileGenerationTestManager from "routes/lens-editor/ai-chats/ProfileGenerationTestManager";
import { caseDisplayGroupsLoader } from "routes/lens-editor/custom-properties/case-display-groups.loader";
import { isOwnerOrSupportLoader } from "routes/users/pattern/isOwnerOrSupport.loader";
import { aiTestsLoader } from "routes/lens-editor/ai-chats/tests-loader";
import { CaseHomeRoute } from "routes/lenses/lens/case/routes/CaseHomeRoute";
import LensStats from "routes/lens-editor/LensStats";
import { lensStatsLoader } from "loaders/lens-stats.loader";
import { tagsLoader } from "loaders/tags.loader";
import { Tags } from "routes/lens-editor/tags/Tags";
import { EntryViewerShim } from "routes/lenses/lens/case/field/entries/EntryViewer";

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements([
    <Route key={1} path="/" element={<Navigate to="/signin" replace />} />,
    <Route key={2} path="/" element={<GridLayout />}>
      {/* Protected */}
      <Route
        element={<ProtectedRoute />}
        loader={appLoader}
        errorElement={<PatternError />}
        shouldRevalidate={shouldRevalidateApp}>
        <Route path="*" element={<NotFound />} />
        <Route path="/deep" element={<Outlet />}>
          <Route
            path="case/:clientCaseId"
            loader={caseExternalLinkLoader}
            shouldRevalidate={shouldRevalidateDeepLinks}
          />
          <Route path="case/:clientCaseId/documents">
            <Route index loader={caseDocumentsExternalLinkLoader} shouldRevalidate={shouldRevalidateDeepLinks} />
            <Route
              path=":documentId"
              loader={caseDocumentExternalLinkLoader}
              shouldRevalidate={shouldRevalidateDeepLinks}
            />
          </Route>
        </Route>
        <Route
          path="/lens"
          element={<Outlet />}
          shouldRevalidate={shouldRevalidateLenses}
          handle={{
            crumb: () => (
              <BreadcrumbDropdownPicker
                search={false}
                pendoId="breadcrumb-lenses"
                label="Lenses"
                noResultsText={undefined}
                currentRoute="/lens"
                key="lenses-breadcrumb"
                options={[
                  {
                    title: "Lenses",
                    route: "/lens",
                    uniqueId: "lenses",
                  },
                ]}
              />
            ),
          }}>
          <Route index element={<Lenses />} />
          <Route path=":lensId" element={<LensRoute />} loader={lensLoader} shouldRevalidate={shouldRevalidateLens}>
            <Route
              element={<Outlet />}
              handle={{
                crumb: () => <LensHomeBreadcrumb key="lenshome-breadcrumb" />,
              }}>
              <Route index element={<LensHome />} />
              {/* case import */}
              <Route
                path="case-import"
                element={<CaseImport />}
                handle={{
                  crumb: () => (
                    <LensSubnavBreadcrumb routeTitle="Import Cases" route="case-import" key="lens-subnav-breadcrumb" />
                  ),
                }}
              />
              <Route
                path="specification"
                id="lens/specification"
                loader={lensSpecLoader}
                shouldRevalidate={shouldRevalidateLens}
                element={<LensSpecification />}
                handle={{
                  crumb: () => (
                    <LensSubnavBreadcrumb routeTitle="Specification" route="specification" key="lens-spec-breadcrumb" />
                  ),
                }}
              />
              <Route
                path="export-config/:configId?"
                element={<ProfileExport />}
                handle={{
                  crumb: () => (
                    <LensSubnavBreadcrumb
                      routeTitle="Case Profile Exports"
                      route="export-config"
                      key="lens-spec-breadcrumb"
                    />
                  ),
                }}
              />
              <Route
                path="files"
                element={<LensFiles />}
                handle={{
                  crumb: () => <LensSubnavBreadcrumb routeTitle="Files" route="files" key="lens-files-breadcrumb" />,
                }}>
                <Route index element={<Navigate to="documents" />} />
                <Route
                  path="documents"
                  element={<LensDocuments />}
                  handle={{
                    crumb: () => (
                      <FilesSubnavBreadcrumb routeTitle="Documents" route="documents" key="lens-documents-breadcrumb" />
                    ),
                  }}
                />
                <Route
                  path="exports"
                  element={<LensExports />}
                  handle={{
                    crumb: () => (
                      <FilesSubnavBreadcrumb routeTitle="Exports" route="exports" key="lens-exports-breadcrumb" />
                    ),
                  }}
                />
              </Route>
              <Route
                path="cases"
                element={<Outlet />}
                handle={{
                  crumb: () => <LensSubnavBreadcrumb routeTitle="Cases" route="cases" key="lens-cases-breadcrumb" />,
                }}>
                <Route index element={<Cases />} />
                <Route
                  path=":caseId"
                  element={<Case />}
                  loader={caseLoader}
                  shouldRevalidate={shouldRevalidateCase}
                  handle={{
                    crumb: () => <CaseHomeBreadcrumb key="case-home-breadcrumb" />,
                  }}>
                  <Route index id="case/home" element={<CaseHomeRoute />} />
                  <Route
                    path="documents"
                    element={<Outlet />}
                    handle={{
                      crumb: () => (
                        <CaseSubnavBreadcrumb
                          routeTitle="Documents"
                          route="documents"
                          key="case-documents-breadcrumb"
                        />
                      ),
                    }}>
                    <Route index element={<Documents />} />
                    <Route
                      path=":documentId"
                      loader={documentReviewLoader}
                      element={<Review />}
                      shouldRevalidate={shouldRevalidateDocumentReviewLoader}
                    />
                  </Route>
                  <Route path="manage-documents" element={<Navigate to="../documents" />} />
                  <Route path="read-quality" element={<ReadQuality />} />
                  <Route
                    path="review"
                    element={<CaseReviewRoute />}
                    handle={{
                      crumb: () => (
                        <CaseSubnavBreadcrumb routeTitle="Review" route="review" key="case-review-breadcrumb" />
                      ),
                    }}
                  />
                  <Route
                    path="profile"
                    element={<Outlet />}
                    handle={{
                      crumb: () => (
                        <CaseSubnavBreadcrumb routeTitle="Profile" route="profile" key="case-profile-breadcrumb" />
                      ),
                    }}>
                    <Route index element={<CaseProfileRoute />} />
                    <Route path=":fieldId" element={<CaseProfileField />}>
                      <Route path="responses/:valueGroupId" element={<EntryViewerShim />} />
                    </Route>
                  </Route>
                </Route>
              </Route>
              <Route
                path="report"
                element={<Outlet />}
                loader={reportLoader}
                handle={{
                  crumb: () => (
                    <LensSubnavBreadcrumb routeTitle="Reports" route="report" key="lens-reports-breadcrumb" />
                  ),
                }}>
                <Route index element={<ReportsHome />} />
                <Route
                  path=":reportId"
                  element={<Report />}
                  handle={{
                    crumb: () => <ReportBreadcrumb key="report-view-breadcrumb" />,
                  }}
                />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="/users" element={<Users />}>
          <Route index element={<Navigate to="/users/pattern" />} />
          <Route path="pattern">
            <Route path=":userId" element={<PatternUser />}>
              <Route path="profile" element={<UserProfile />} />
              <Route path="lenses" element={<UserLensAccess />} />
              <Route path="api" loader={isOwnerOrSupportLoader} element={<UserApiAccess />} />
              <Route path="advanced" element={<UserAdvanced />} />
              <Route index element={<Navigate to="profile" replace />} />
            </Route>
            <Route path="invite" element={<PatternInvite />} />
            <Route index element={<PatternUsers />} />
          </Route>
          <Route path="collect">
            <Route path="invite" element={<CollectInvites />} />
            <Route index element={<CollectUsers />} />
          </Route>
        </Route>
        <Route
          path="/dashboard"
          element={<Dashboard />}
          loader={dashboardLoader}
          shouldRevalidate={shouldRevalidateDashboard}
        />
        <Route
          path="/lens-editor"
          loader={lensEditorLensesLoader}
          shouldRevalidate={shouldRevalidateLenses}
          element={<Outlet />}
          handle={{
            crumb: () => [
              <LensEditorBreadcrumb key="lens-editor-client_name-breadcrumb" />,
              <BreadcrumbDropdownPicker
                key="lens-editor-lenses-breadcrumb"
                search={false}
                currentRoute="/lens-editor"
                options={[
                  {
                    title: "Lenses",
                    route: "/lens-editor",
                    uniqueId: "lenses",
                  },
                ]}
                label="Lenses"
                noResultsText={undefined}
              />,
            ],
          }}>
          <Route index element={<LensEditorLenses />} />
          <Route
            path=":lensId"
            element={<LensEditor />}
            loader={lensEditorLoader}
            shouldRevalidate={shouldRevalidateLensEditor}
            handle={{
              crumb: () => <LensEditorHomeBreadcrumb key="lens-editor-breadcrumb" />,
            }}>
            <Route index element={<Navigate to="profile" replace />} />
            <Route
              path="rules"
              element={<LensRules />}
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb routeTitle="Rules" route="rules" key="lens-editor-rules-breadcrumb" />
                ),
              }}
            />
            <Route
              path="profile"
              element={<ProfileBuilderRoot />}
              loader={customPropertiesLoader}
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb
                    routeTitle="Profile"
                    route="profile"
                    key="lens-editor-profile-breadcrumb"
                  />
                ),
              }}>
              <Route index element={<Navigate to="sections/all" replace />} />
              <Route path="sections" element={<Outlet />}>
                <Route path=":sectionId" element={<Outlet />}>
                  <Route index element={<ProfileBuilderSections />} />
                  <Route path="fields/:fieldId" element={<ProfileBuilderField />} loader={lenseditorProfileCellLoader}>
                    <Route index element={<Navigate to="cells" replace />} />
                    <Route element={<ProfileBuilderFieldWrapper />}>
                      <Route path="settings" element={<ProfileBuilderFieldSettings />} />
                      <Route path="cells">
                        <Route index element={<ProfileBuilderCells />} />
                      </Route>
                      <Route path="cells/:cellId" element={<ProfileBuilderCell />} />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>
            <Route
              path="duplicates"
              element={<Duplicates />}
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb
                    routeTitle="Duplicates"
                    route="duplicates"
                    key="lens-editor-duplicates-breadcrumb"
                  />
                ),
              }}
            />
            <Route
              path="chronology"
              element={<ChronologyExportBuilder />}
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb
                    routeTitle="Chronology"
                    route="chronology"
                    key="lens-editor-chronology-breadcrumb"
                  />
                ),
              }}
            />
            <Route
              path="page-labels"
              id="lens-editor/lens/page-labels"
              action={pageLabelsAction}
              loader={pageLabelsLoader}
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb
                    routeTitle="Page Labels"
                    route="page-labels"
                    key="lens-editor-page_labels-breadcrumb"
                  />
                ),
              }}
              element={<PageLabelsHeader />}>
              <Route index element={<Navigate to="list" replace />} />
              <Route id="lens-editor/lens/page-labels-list" path="list" element={<PageLabels />}>
                <Route path=":pageLabelId" element={<PageLabelRoute />} action={pageLabelRuleAction} />
              </Route>
              <Route
                id="lens-editor/lens/page-labels-import"
                path="import"
                element={<PageLabelsImport />}
                action={pageLabelsImportAction}
              />
              <Route id="lens-editor/lens/system" path="system" element={<SystemPageLabels />} />
            </Route>
            <Route
              path="scripts"
              id="lens-editor/lens/scripts"
              element={<ScriptsHeader />}
              loader={scriptsLoader}
              action={scriptsAction}
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb
                    routeTitle="Scripts"
                    route="scripts"
                    key="lens-editor-scripts-breadcrumb"
                  />
                ),
              }}>
              <Route index element={<Navigate to="list" replace />} />
              <Route id="lens-editor/lens/scripts-list" path="list" element={<Scripts />}>
                <Route path=":scriptName" element={<ScriptRoute />} />
              </Route>
              <Route
                loader={scriptsHistoryLoader}
                id="lens-editor/lens/scripts-history"
                path="history"
                element={<ScriptsHistory />}
              />
            </Route>
            <Route
              path="settings"
              id="lens-editor/lens/settings"
              element={<LensSettings />}
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb
                    routeTitle="Settings"
                    route="settings"
                    key="lens-editor-settings-breadcrumb"
                  />
                ),
              }}
            />
            <Route
              path="document-properties"
              element={<CustomDocumentProperties />}
              loader={customPropertiesLoader}
              id="lens-editor/lens/properties-document"
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb
                    routeTitle="Document Properties"
                    route="document-properties"
                    key="lens-editor-document_properties-breadcrumb"
                  />
                ),
              }}
            />
            <Route
              path="case-properties"
              element={<CasePropertiesHeader />}
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb
                    routeTitle="Case Properties"
                    route="case-properties"
                    key="lens-editor-case_properties-breadcrumb"
                  />
                ),
              }}
              loader={customPropertiesLoader}>
              <Route index element={<Navigate to="case" replace />} />
              <Route id="lens-editor/lens/properties-case" path="case" element={<CustomCaseProperties />} />
              <Route
                id="lens-editor/lens/properties-groups"
                path="groups"
                element={<DisplayGroups />}
                loader={caseDisplayGroupsLoader}>
                <Route path=":caseDisplayGroupName" element={<CaseDisplayGroup />} />
              </Route>
              <Route id="lens-editor/lens/properties-locations" path="locations" element={<GroupLocations />} />
              <Route id="lens-editor/lens/properties-calc" path="calcs" element={<CustomCalcProperties />} />
            </Route>
            <Route
              path="ai"
              element={<AITab />}
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb
                    routeTitle="AI Review"
                    route="ai"
                    key="lens-editor-ai_review-breadcrumb"
                  />
                ),
              }}>
              <Route index element={<Navigate to="profile-generation" replace />} />
              <Route path="profile-generation" element={<ProfileGenerationRoute />} />
              <Route path="chats" element={<AiChatBrowser />}>
                <Route
                  path=":chatId"
                  loader={aiChatLoader}
                  shouldRevalidate={shouldRevalidateAiChat}
                  element={<AiChat />}
                />
              </Route>
              <Route path="tests" element={<ProfileGenerationTestsRoute />}>
                <Route path=":fieldId" loader={aiTestsLoader}>
                  <Route index element={<Navigate to="list" replace />} />
                  <Route path="list" element={<ProfileGenerationTestManager />} />
                </Route>
              </Route>
            </Route>
            <Route
              id="lens-editor/lens/stats"
              path="stats"
              element={<LensStats />}
              loader={lensStatsLoader}
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb
                    routeTitle="Lens Run Status"
                    route="stats"
                    key="lens-editor-stats-breadcrumb"
                  />
                ),
              }}
            />
            <Route
              id="lens-editor/lens/tags"
              path="tags"
              element={<Tags />}
              loader={tagsLoader}
              handle={{
                crumb: () => (
                  <LensEditorSubnavBreadcrumb routeTitle="Tags" route="tags" key="lens-editor-tags-breadcrumb" />
                ),
              }}
            />
          </Route>
          <Route path="stats" element={<Stats />} />
        </Route>
        <Route path="/import" element={<Import />}>
          <Route index element={<Navigate to="history" replace />} />
          <Route path="firms" element={<FirmManagement />} />
          <Route path="history" element={<ImportHistory />} />
        </Route>
      </Route>
      {/* Unprotected */}
      <Route path="/scratch" element={<ScratchView />} />
    </Route>,
    <Route key={3} path="*" element={<Navigate to="/signin" replace />} />,
    <Route key={4} path="/signin" element={<Login />} loader={checkSignedInLoader} errorElement={<PatternError />} />,
    <Route key={5} path="/forgot-password" element={<ForgotPassword />} errorElement={<PatternError />} />,
    <Route key={6} path="/reset-password" element={<ResetPassword />} errorElement={<PatternError />} />,
    <Route key={7} path="/help" element={<Help />} errorElement={<PatternError />} />,
    <Route key={8} path="/logout" element={<Logout />} errorElement={<PatternError />} />,
    <Route key={9} path="/onboarding" element={<Onboarding />} />,
  ])
);

export default router;
